import { ContentTypeDefault, MediaTypes } from '@setplex/tria-api'
import { produce, type Draft } from 'immer'
import type { Favorites } from './index.h'

/**
 * Small helper function to get favorite jar type from `contentType` field
 */
export function getType(from: MediaTypes): keyof Favorites | undefined {
  switch (from) {
    case MediaTypes.LIVE_CHANNEL:
      return 'live_channel'
    case MediaTypes.VIDEO:
      return 'video'
  }
}

/**
 * Small helper function to get favorite jar type from `mixedType` field
 *
 * Content set items are a mix of movies and tv shows with `contentType` always
 * set to `VIDEO`, so `mixedType` is the only field telling them apart - the same
 * field the card itself resolves its type from (`entities/tags/lib.ts`)
 */
export function getTypeFromMixed(
  from: ContentTypeDefault
): keyof Favorites | undefined {
  switch (from) {
    case ContentTypeDefault.movie:
      return 'video'
    case ContentTypeDefault.tvshow:
      return 'series'
  }
}

/**
 * Update parts of favorite jar, used in store reducers
 *
 * @param type - jar type to update
 * @returns curried `produce` function, which is used as reduser to change Favorites
 */
export function update(type?: keyof Favorites | undefined) {
  return produce((draft: Draft<Favorites>, payload: unknown) => {
    traverse(draft, payload, type)
  })
}

/**
 * Recursive helper function to update parts of favorite jar
 *
 * @param draft - draft of Favorites, got from `produce` to change
 * @param obj - any object with any structure
 * @param type - jar type to search
 * @returns nothing, recursively updates `draft` instead
 */
function traverse(
  draft: Draft<Favorites>,
  obj: unknown,
  type?: keyof Favorites | undefined
) {
  // end of recursive branch, stop recursion here
  if (obj == null) {
    return
  }

  // if traversed object is an array - recursively traverse each element
  if (Array.isArray(obj)) {
    obj.forEach((o) => traverse(draft, o, type))
  }

  // if traversed object is an object
  if (typeof obj === 'object') {
    // and has 'contentType' field - it will override given type for all branches below
    if ('contentType' in obj) {
      const newType = getType(obj.contentType as MediaTypes)
      if (newType) {
        type = newType
      }
    }

    // and has 'mixedType' field - it is the real type of a mixed list item,
    // so it wins over 'contentType' for all branches below
    if ('mixedType' in obj) {
      const newType = getTypeFromMixed(obj.mixedType as ContentTypeDefault)
      if (newType) {
        type = newType
      }
    }

    // and has 'favorite' and 'id' fields - consider this is a required entity we seek
    //  -> get type and update given entity in favorite jar
    if ('isFavorite' in obj && 'id' in obj) {
      if (type) {
        draft[type][obj.id as string | number] = Boolean(obj.isFavorite)
      }
    }

    // otherwise recursively traverse each value from object
    else {
      Object.values(obj).forEach((o) => traverse(draft, o, type))
    }
  }
}
